import React, { useEffect, useRef } from 'react'
import Plotly from 'plotly.js-basic-dist-min'
import debounce from 'debounce'

// Note: plotly-basic-dist-min includes pt-BR locale by default
// No need to register separately

export interface NewPlot {
  data: Plotly.Data[]
  layout: Partial<Plotly.Layout>
}

export interface PlotProps extends NewPlot {}

export default function Plot({ data, layout }: PlotProps) {
  const ref = useRef<HTMLDivElement>(null)
  const hasInitializedRef = useRef(false)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const relayoutRef = useRef<ReturnType<typeof debounce> | null>(null)

  useEffect(() => {
    const plotElement = ref.current
    if (!plotElement) return

    let wasUnmounted = false

    if (!hasInitializedRef.current) {
      Plotly.newPlot(plotElement, data, layout, { locale: 'pt-BR' })

      const relayout = debounce(() => {
        if (wasUnmounted) return
        if (!plotElement.isConnected) return
        if (!(plotElement as any)._fullLayout) return
        if (plotElement.clientWidth <= 0 || plotElement.clientHeight <= 0) return

        void Plotly.relayout(plotElement, {
          width: plotElement.clientWidth,
          height: plotElement.clientHeight,
        }).catch(error => {
          if (!wasUnmounted) {
            console.warn('Plotly relayout failed', error)
          }
        })
      }, 150)

      const resizeObserver = new ResizeObserver(relayout)

      resizeObserver.observe(plotElement)

      relayoutRef.current = relayout
      resizeObserverRef.current = resizeObserver
      hasInitializedRef.current = true

      return () => {
        wasUnmounted = true
        relayout.clear()
        resizeObserver.disconnect()
        try {
          Plotly.purge(plotElement)
        } catch (error) {
          console.warn('Plotly purge failed', error)
        }
      }
    }

    Plotly.react(plotElement, data, layout, { locale: 'pt-BR' })
  }, [data, layout])

  useEffect(() => {
    return () => {
      const plotElement = ref.current
      relayoutRef.current?.clear()
      resizeObserverRef.current?.disconnect()

      if (plotElement) {
        Plotly.purge(plotElement)
      }
    }
  }, [])

  return (
    <div
      style={{
        position: 'relative',
        height: layout.height || 'auto',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        ref={ref}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />
    </div>
  )
}
